import React from 'react';
import { Row, Col, Button, Form, Select, Space } from 'antd';
import ToastNotification from '../../utils/ToastNotification';
import { Client } from '../../types/Client';

interface StackListSearchFiltersInterfaceProps {
    wrapperStyleClass?: string;
    isLoadingData: boolean;
    clients: Client[];
    onSetSearchFilters(filters: any, opts?: { isPaging: boolean }): void;
}

const StackListSearchFilters: React.FC<StackListSearchFiltersInterfaceProps> = (
    props: StackListSearchFiltersInterfaceProps,
): React.ReactElement => {
    const { wrapperStyleClass, isLoadingData, clients, onSetSearchFilters: setFiltersToApply } = props;

    const [form] = Form.useForm();

    const clientList = clients
        .map((client: { id: number; name: string }) => ({
            value: client.id,
            key: client.id,
            text: client.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text));

    const hasValidFilter = (filters: { [key: string]: unknown }): boolean => {
        return Object.values(filters).some((filter) => typeof filter !== 'undefined');
    };

    const removeDirtyFilters = (filters: { [key: string]: any }): { [key: string]: any } => {
        const filtersToApply = Object.assign(filters);
        Object.keys(filtersToApply).forEach(
            (key) =>
                (!filtersToApply[key] || (Array.isArray(filtersToApply[key]) && filtersToApply[key].length === 0)) &&
                delete filtersToApply[key],
        );
        return filtersToApply;
    };

    const setSearchFilters = (): void => {
        const filters = removeDirtyFilters(form.getFieldsValue());
        if (hasValidFilter(filters)) {
            setFiltersToApply({ ...filters });
            return;
        }
        setFiltersToApply({});
        ToastNotification({
            message: 'No search criteria',
            description: 'Stack list will be reloaded.',
        });
    };

    const resetFilters = (): void => {
        form.resetFields();
        setFiltersToApply({});
    };

    return (
        <div className={wrapperStyleClass || 'px-4 bg-white'}>
            <Form layout="vertical" form={form} id="filter-form" name="filter-form">
                <Row gutter={24}>
                    <Col span={6} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 6 }} title="Clients">
                        <Form.Item label="Clients" id="client_ids" name="client_ids" valuePropName="client_ids">
                            <Select
                                id="client_ids"
                                mode="multiple"
                                placeholder="Select clients"
                                style={{ width: '100%' }}
                                optionFilterProp="children"
                                filterOption={(input, option): boolean =>
                                    option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                showArrow
                            >
                                {clientList.map((item: { value: any; key: number; text: string }) => (
                                    <Select.Option key={item.key} value={item.value}>
                                        {item.text}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col
                        span={18}
                        xs={{ span: 24 }}
                        sm={{ span: 24 }}
                        md={{ span: 18 }}
                        className="d-flex flex-row align-items-center justify-content-start"
                    >
                        <Space>
                            <Button
                                id="btn-search"
                                name="btn-search"
                                title="Search"
                                onClick={setSearchFilters}
                                disabled={isLoadingData}
                                className="ant-btn text-white btn-primary bg-primary"
                            >
                                Search
                            </Button>
                            <Button
                                id="btn-reset-filters"
                                name="btn-reset-filters"
                                title="Reset"
                                onClick={resetFilters}
                                disabled={isLoadingData}
                                className="ant-btn"
                            >
                                Reset
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default StackListSearchFilters;
